/**
 * File types aligned with backend definitions
 * Source: law-firms-backend/src/files/constants/file-types.constants.ts
 */

export enum FileCategory {
  DECEDENTS_ASSETS = 'decedents-assets',
  MATTERS_ASSETS = 'matters-assets',
  CONTACTS_ASSETS = 'contacts-assets',
  INSTITUTIONS_ASSETS = 'institutions-assets',
  CORRESPONDENCES_ASSETS = 'correspondences-assets',
  LAW_FIRM_ASSETS = 'law-firm-assets',
  LAW_FIRM_USER_ASSETS = 'law-firm-user-assets'
}

export enum FileTypeSlug {
  PRIMARY_ID = 'primary-id',
  SECONDARY_ID = 'secondary-id',
  DEATH_CERTIFICATE = 'death-certificate',
  WILL = 'will',
  CODICIL = 'codicil',
  CORRESPONDENCE_ATTACHMENT = 'correspondence-attachment',
  INSTITUTION_FORM_TEMPLATE = 'institution-form-template',
  INSTITUTION_FORM_SIGNED = 'institution-form-signed',
  LETTERHEAD = 'letterhead',
  HANDWRITTEN_SIGNATURE = 'handwritten-signature',
  EMAIL_SIGNATURE = 'email-signature',
  GRANT_OF_representation = 'grant-of-representation',
  ADDITIONAL_DOCUMENT = 'additional-document'
}

export interface FileTypeConfig {
  name: string;
  slug: FileTypeSlug;
  category: FileCategory;
  requiresProcessing: boolean;
  requiresMatter: boolean;
}

export const FILE_TYPES: Record<FileTypeSlug, FileTypeConfig> = {
  [FileTypeSlug.PRIMARY_ID]: {
    name: 'Primary ID',
    slug: FileTypeSlug.PRIMARY_ID,
    category: FileCategory.CONTACTS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.SECONDARY_ID]: {
    name: 'Secondary ID',
    slug: FileTypeSlug.SECONDARY_ID,
    category: FileCategory.CONTACTS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.CODICIL]: {
    name: 'Codicil',
    slug: FileTypeSlug.CODICIL,
    category: FileCategory.DECEDENTS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.DEATH_CERTIFICATE]: {
    name: 'Death Certificate',
    slug: FileTypeSlug.DEATH_CERTIFICATE,
    category: FileCategory.DECEDENTS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.WILL]: {
    name: 'Will',
    slug: FileTypeSlug.WILL,
    category: FileCategory.DECEDENTS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.CORRESPONDENCE_ATTACHMENT]: {
    name: 'Correspondence Attachment',
    slug: FileTypeSlug.CORRESPONDENCE_ATTACHMENT,
    category: FileCategory.CORRESPONDENCES_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.INSTITUTION_FORM_TEMPLATE]: {
    name: 'Institution Form Template',
    slug: FileTypeSlug.INSTITUTION_FORM_TEMPLATE,
    category: FileCategory.INSTITUTIONS_ASSETS,
    requiresProcessing: true,
    requiresMatter: false
  },
  [FileTypeSlug.INSTITUTION_FORM_SIGNED]: {
    name: 'Institution Form Signed',
    slug: FileTypeSlug.INSTITUTION_FORM_SIGNED,
    category: FileCategory.MATTERS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.LETTERHEAD]: {
    name: 'Letterhead',
    slug: FileTypeSlug.LETTERHEAD,
    category: FileCategory.LAW_FIRM_ASSETS,
    requiresProcessing: true,
    requiresMatter: false
  },
  [FileTypeSlug.HANDWRITTEN_SIGNATURE]: {
    name: 'Handwritten Signature',
    slug: FileTypeSlug.HANDWRITTEN_SIGNATURE,
    category: FileCategory.LAW_FIRM_USER_ASSETS,
    requiresProcessing: true,
    requiresMatter: false
  },
  [FileTypeSlug.EMAIL_SIGNATURE]: {
    name: 'Email Signature',
    slug: FileTypeSlug.EMAIL_SIGNATURE,
    category: FileCategory.LAW_FIRM_ASSETS,
    requiresProcessing: true,
    requiresMatter: false
  },
  [FileTypeSlug.GRANT_OF_representation]: {
    name: 'Grant of Representation',
    slug: FileTypeSlug.GRANT_OF_representation,
    category: FileCategory.MATTERS_ASSETS,
    requiresProcessing: true,
    requiresMatter: true
  },
  [FileTypeSlug.ADDITIONAL_DOCUMENT]: {
    name: 'Additional Document',
    slug: FileTypeSlug.ADDITIONAL_DOCUMENT,
    category: FileCategory.MATTERS_ASSETS,
    requiresProcessing: false,
    requiresMatter: true
  }
};

// Detect file type from filename
export function detectFileTypeFromName(filename: string): FileTypeSlug | null {
  const lowerName = filename.toLowerCase();

  // Death certificate patterns
  if (
    /death[\s\-_]?cert/i.test(lowerName) ||
    /certificate[\s\-_]?of[\s\-_]?death/i.test(lowerName)
  ) {
    return FileTypeSlug.DEATH_CERTIFICATE;
  }

  // Will patterns
  if (/will/i.test(lowerName) && !/william/i.test(lowerName)) {
    return FileTypeSlug.WILL;
  }

  // Grant of representation patterns
  if (/grant/i.test(lowerName) || /probate/i.test(lowerName)) {
    return FileTypeSlug.GRANT_OF_representation;
  }

  // ID patterns
  if (
    /passport/i.test(lowerName) ||
    /drivers?[\s\-_]?licen[cs]e/i.test(lowerName) ||
    /birth[\s\-_]?cert/i.test(lowerName)
  ) {
    return FileTypeSlug.PRIMARY_ID;
  }

  if (/medicare/i.test(lowerName) || /health[\s\-_]?card/i.test(lowerName)) {
    return FileTypeSlug.SECONDARY_ID;
  }

  // Institution form patterns
  if (/signed/i.test(lowerName) && /form/i.test(lowerName)) {
    return FileTypeSlug.INSTITUTION_FORM_SIGNED;
  }

  // IMPORTANT: Never return CORRESPONDENCE_ATTACHMENT here as it requires
  // an attachmentId which we don't have in the general document upload flow
  // Default to ADDITIONAL_DOCUMENT for general matter documents
  return FileTypeSlug.ADDITIONAL_DOCUMENT;
}

/**
 * The three core document slots, in the order the Documents tab renders them,
 * with the label each is called by everywhere a user sees it (VALE-806).
 *
 * One list, bound to `FILE_TYPES` rather than restating its names: the "Mark
 * as …" menu items, the success toast, and anything else naming a slot all read
 * from here, so a renamed document type cannot leave one surface saying
 * something else.
 */
export const CORE_SLOT_TYPES = [
  'will',
  'death-certificate',
  'grant-of-representation'
] as const;

export const CORE_SLOT_LABELS: Record<
  (typeof CORE_SLOT_TYPES)[number],
  string
> = {
  [FileTypeSlug.WILL]: FILE_TYPES[FileTypeSlug.WILL].name,
  [FileTypeSlug.DEATH_CERTIFICATE]:
    FILE_TYPES[FileTypeSlug.DEATH_CERTIFICATE].name,
  [FileTypeSlug.GRANT_OF_representation]:
    FILE_TYPES[FileTypeSlug.GRANT_OF_representation].name
};

/** The two contact-level identity document slots (VALE-806). */
export const IDENTITY_SLOT_TYPES = ['primary-id', 'secondary-id'] as const;

export function isIdentitySlotType(
  fileTypeSlug: string
): fileTypeSlug is (typeof IDENTITY_SLOT_TYPES)[number] {
  return (IDENTITY_SLOT_TYPES as readonly string[]).includes(fileTypeSlug);
}
